/**
 * The pieces the Points step's map editor shares between the Modbus register
 * table (RegisterBrowser) and the other drivers' point picker (PointPicker),
 * so both read and behave the same:
 *
 * - {@link MapFileButtons}: "Import CSV/JSON", "Export" (CSV or JSON) and the
 *   column-format help toggle, for the step's toolbar. Import reads the file
 *   and hands back a preview; nothing is applied yet.
 * - {@link ImportPreview}: the file's rows with their errors, then
 *   Replace rows / Append rows / Cancel.
 * - {@link MapFormatHelp}: the columns this driver's map takes.
 * - {@link WordOrderPicker}, {@link ScaleInput}: the row editors.
 * - {@link MapFooter}: "N selected · ..." and "Bring in selected".
 * - {@link KnownMapPicker}: "…or load a known map" from the platform's
 *   library (GET /v1/things/point-maps), into the same preview as a file.
 *
 * The file format is lib/iot/point-map-io (one schema for every driver).
 */

import { useRef, useState, type ChangeEvent } from "react";
import { useQuery } from "@tanstack/react-query";
import { ArrowRight, ChevronDown, CircleHelp, Download, FileUp, Library, Loader2 } from "lucide-react";
import { Badge } from "@/lib/ui/badge";
import { Button } from "@/lib/ui/button";
import { Input } from "@/lib/ui/input";
import { NativeSelect } from "@/lib/ui/native-select";
import { cn } from "@/lib/ui/cn";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/lib/ui/dropdown-menu";
import { isLiveApi, things, type ModbusWordOrder, type PointDataType } from "@/lib/api";
import { mapMatchesProduct, orderForDevice, preselectedMap } from "@/lib/iot/known-maps";
import { downloadBlob, downloadCsv } from "@/lib/reports/export-csv";
import { driverLabel, isModbusDriver } from "@/lib/iot/drivers";
import { WORD_ORDER_CHOICES } from "@/lib/iot/register-decode";
import {
  OPTIONAL_COLUMNS,
  mapColumns,
  parsePointMap,
  pointIdentity,
  pointMapToCsv,
  pointMapToJson,
  type ImportResult,
  type PointRecord,
} from "@/lib/iot/point-map-io";

export type ImportMode = "append" | "replace";

/** A read file awaiting the operator's choice. */
export type MapPreview = ImportResult & { file: string };

function fileBase(label: string): string {
  const slug = label
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
  return `${slug || "device"}-point-map`;
}

/** Toolbar buttons: import a map file (to a preview), export the current map, show the format help. */
export function MapFileButtons({
  driverId,
  dataTypes,
  records,
  deviceLabel,
  helpOpen,
  onHelp,
  onPreview,
  onError,
}: {
  driverId: string;
  /** The types the driver decodes, for checking imported rows. */
  dataTypes?: readonly PointDataType[];
  /** The map as it stands (what would be brought in), for export. */
  records: PointRecord[];
  /** Names the downloaded file ("PLC 10.2.10.40" -> plc-10-2-10-40-point-map.csv). */
  deviceLabel: string;
  helpOpen: boolean;
  onHelp: (open: boolean) => void;
  onPreview: (p: MapPreview) => void;
  /** The file could not be read as a point map. */
  onError: (message: string) => void;
}) {
  const fileRef = useRef<HTMLInputElement>(null);
  const base = fileBase(deviceLabel);
  const empty = records.length === 0;

  const onFile = async (e: ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0];
    e.target.value = ""; // the same file can be chosen again
    if (!f) return;
    try {
      const text = await f.text();
      const format = /\.json$/i.test(f.name) ? "json" : /\.csv$/i.test(f.name) ? "csv" : undefined;
      const res = parsePointMap(text, { driver: driverId, dataTypes }, format);
      if (res.error) onError(`${f.name}: ${res.error}`);
      else onPreview({ ...res, file: f.name });
    } catch {
      onError(`Could not read ${f.name}.`);
    }
  };

  return (
    <>
      <input
        ref={fileRef}
        type="file"
        accept=".csv,.json,text/csv,application/json"
        className="hidden"
        onChange={(e) => void onFile(e)}
        aria-label="Point map file (CSV or JSON)"
        tabIndex={-1}
      />
      <Button variant="outline" onClick={() => fileRef.current?.click()}>
        <FileUp /> Import CSV/JSON
      </Button>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="outline" disabled={empty} title={empty ? "Select at least one row to export the map." : undefined}>
            <Download /> Export <ChevronDown className="size-3.5" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="start">
          <DropdownMenuItem onSelect={() => downloadCsv(pointMapToCsv(driverId, records), `${base}.csv`)}>
            CSV
          </DropdownMenuItem>
          <DropdownMenuItem
            onSelect={() =>
              downloadBlob(pointMapToJson(driverId, records), `${base}.json`, "application/json;charset=utf-8")
            }
          >
            JSON
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
      <Button
        variant="ghost"
        size="icon"
        aria-label="Map file columns"
        aria-expanded={helpOpen}
        title="Map file columns"
        onClick={() => onHelp(!helpOpen)}
      >
        <CircleHelp />
      </Button>
    </>
  );
}

/**
 * "…or load a known map": the platform's known point maps for this driver
 * (the VisiPour auto pour station over EtherNet/IP, ...). A chosen map is read
 * with the same parser as an imported file and handed to the same preview, so
 * nothing is applied until Replace / Append. A map built from the device's
 * product (its scan identity) is listed first and preselected when it is the
 * only one; it matches the hardware, not the program, so the operator still
 * loads it. Renders nothing when the library has no map for the driver.
 */
export function KnownMapPicker({
  driverId,
  dataTypes,
  deviceProduct,
  onPreview,
  onError,
}: {
  driverId: string;
  /** The types the driver decodes, for checking the map's rows. */
  dataTypes?: readonly PointDataType[];
  /** The device's product name as the scan reported it (identity.product), if any. */
  deviceProduct?: string;
  onPreview: (p: MapPreview) => void;
  onError: (message: string) => void;
}) {
  const [chosenId, setChosenId] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const query = useQuery({
    queryKey: ["connect", "point-maps", driverId],
    queryFn: () => things.pointMaps(driverId),
    enabled: isLiveApi && !!driverId,
    staleTime: 5 * 60_000,
  });
  const maps = orderForDevice(query.data?.maps ?? [], deviceProduct);
  // Without the live API the list is never fetched, so this renders nothing.
  if (maps.length === 0) return null;

  const id = chosenId ?? preselectedMap(maps, deviceProduct);
  const chosen = maps.find((m) => m.id === id);

  const load = async () => {
    if (!chosen) return;
    setLoading(true);
    try {
      const doc = await things.pointMap(chosen.id);
      const res = parsePointMap(JSON.stringify(doc), { driver: driverId, dataTypes }, "json");
      if (res.error) onError(`${chosen.title}: ${res.error}`);
      else onPreview({ ...res, file: chosen.title });
    } catch {
      onError(`Could not load the map ${chosen.title}.`);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex w-full flex-col gap-1.5">
      <label htmlFor={`known-map-${driverId}`} className="text-xs font-medium text-on-surface">
        …or load a known map
      </label>
      <div className="flex flex-wrap items-center gap-2">
        <NativeSelect
          id={`known-map-${driverId}`}
          value={id}
          onChange={(e) => setChosenId(e.target.value)}
          className="h-9 min-w-56 max-w-full flex-1 text-sm"
        >
          <option value="">Select a machine…</option>
          {maps.map((m) => (
            <option key={m.id} value={m.id}>
              {m.title} ({m.point_count} points){mapMatchesProduct(m, deviceProduct) ? " · matches this controller" : ""}
            </option>
          ))}
        </NativeSelect>
        <Button variant="outline" onClick={() => void load()} disabled={!chosen || loading}>
          {loading ? <Loader2 className="animate-spin" /> : <Library />}
          Load map
        </Button>
      </div>
      {chosen && (
        <p className="text-[11px] text-on-surface-muted">
          {chosen.model ? <span className="text-on-surface">{chosen.model}. </span> : null}
          {chosen.description}
          {mapMatchesProduct(chosen, deviceProduct)
            ? " Built for this controller model; check it runs the same program before you bring the points in."
            : ""}
        </p>
      )}
    </div>
  );
}

/** What each address column holds, for the format help. */
const COLUMN_HELP: Record<string, string> = {
  space: "holding (default), input, coil or discrete",
  register: "0-based register (holding 40001 is 0)",
  word_order: "big (ABCD), little (DCBA), cdab or badc; for 32- and 64-bit values",
  node_id: "ns=2;s=Tank.Level, or nsu=<namespace URI>;s=...",
  area: "DB, M, I or Q",
  db: "data block number (area DB only)",
  byte: "byte offset in the area",
  bit: "0 to 7, for a bool",
  tag: "controller tag; program tags as Program:Main.Name",
  elem: "array element",
  device: "D100, M10, X1F (X Y B W SB SW DX DY are hex)",
  key: "the value's key in each result, e.g. active_clay_content, or instrument_ok",
};

const EXAMPLE: Record<string, string> = {
  modbus:
    "driver,name,space,register,type,word_order,unit,scale\nmodbus-tcp,weight,holding,0,float32,big,kg,\nmodbus-tcp,age,holding,5,uint16,,s,0.1",
  opcua: "driver,name,node_id,type,unit,scale\nopcua,tank_level,ns=2;s=Tank.Level,float64,m,",
  s7: "driver,name,area,db,byte,bit,type,unit,scale\ns7,bath_temp,DB,1,0,,float32,°C,\ns7,pump_on,M,,2,3,bool,,",
  enip: "driver,name,tag,elem,type,unit,scale\nenip,line_speed,Line.Speed,,float32,m/min,",
  melsec: "driver,name,device,type,word_order,unit,scale\nmelsec,count,D100,uint32,little,,",
  "results-feed":
    "driver,name,key,type,unit,scale,max_silence_sec\nresults-feed,active_clay_content,active_clay_content,float64,%,,604800\nresults-feed,instrument_ok,instrument_ok,bool,,,",
};

/** The columns a map for this driver takes, with an example. */
export function MapFormatHelp({ driverId, dataTypes }: { driverId: string; dataTypes?: readonly PointDataType[] }) {
  const modbus = isModbusDriver(driverId);
  const types = modbus
    ? "int16, uint16, int32, uint32, float32, float64 (double); bool for a coil or discrete input"
    : (dataTypes?.length ? dataTypes : ["bool", "int16", "uint16", "int32", "uint32", "float32", "float64", "string"]).join(", ");
  const common: Record<string, string> = {
    driver: `optional; ${driverLabel(driverId)} when set`,
    name: "metric name, required: letters, digits, _ . -",
    type: types,
    unit: "optional, e.g. kg",
    scale: "optional multiplier, e.g. 0.1 (empty means 1)",
    poll_sec: "optional, this point's poll interval in whole seconds, 1 to 86400 (empty means the device's)",
    deadband: "optional, publish only when the value moves more than this (empty means every read)",
    max_silence_sec:
      "optional, how long the last value reads as current, whole seconds 60 to 604800 (a minute to a week); empty means a few polls",
  };
  return (
    <section
      aria-label="Map file columns"
      className="flex flex-col gap-2 rounded-md border border-border bg-surface p-3 text-xs text-on-surface-muted"
    >
      <p>
        One row per point, the first row the column names. CSV, or JSON with the same names as{" "}
        <span className="font-mono">{'{"points": [ ... ]}'}</span>. CSV lines starting with # are comments.
      </p>
      <dl className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-0.5">
        {[...mapColumns(driverId), ...OPTIONAL_COLUMNS].map((c) => (
          <div key={c} className="contents">
            <dt className="font-mono text-on-surface">{c}</dt>
            <dd>{common[c] ?? COLUMN_HELP[c] ?? ""}</dd>
          </div>
        ))}
      </dl>
      <pre className="overflow-x-auto rounded bg-surface-muted/60 p-2 font-mono text-[11px] text-on-surface">
        {EXAMPLE[modbus ? "modbus" : driverId] ?? mapColumns(driverId).join(",")}
      </pre>
    </section>
  );
}

/** A read file's rows with their errors; the valid ones replace or are appended to the list. */
export function ImportPreview({
  preview,
  driverId,
  listed,
  onApply,
  onCancel,
}: {
  preview: MapPreview;
  driverId: string;
  /** Addresses already listed (pointIdentity), so a row can say it updates one. */
  listed: ReadonlySet<string>;
  onApply: (records: PointRecord[], mode: ImportMode) => void;
  onCancel: () => void;
}) {
  const valid = preview.rows.flatMap((r) => (r.record ? [r.record] : []));
  const errors = preview.rows.length - valid.length;
  return (
    <section
      aria-label="Import preview"
      className="flex flex-col gap-3 rounded-lg border border-border bg-surface-elevated p-3"
    >
      <div className="flex flex-wrap items-center gap-2 text-sm">
        <span className="font-mono font-medium text-on-surface">{preview.file}</span>
        <Badge variant={errors > 0 ? "warning" : "success"} className="text-[10px]">
          {preview.rows.length} row{preview.rows.length === 1 ? "" : "s"} · {errors} error{errors === 1 ? "" : "s"}
        </Badge>
        {errors > 0 && <span className="text-xs text-on-surface-muted">Rows with errors are left out.</span>}
      </div>
      {preview.ignoredColumns.length > 0 && (
        <p className="text-xs text-on-surface-muted">
          Columns not used for {driverLabel(driverId)}: {preview.ignoredColumns.join(", ")}.
        </p>
      )}
      <div className="max-h-72 overflow-auto rounded-md border border-border">
        <table className="w-full text-xs">
          <thead className="sticky top-0 bg-surface-muted text-left text-[11px] uppercase tracking-wide text-on-surface-muted">
            <tr>
              <th scope="col" className="px-2 py-1.5">
                Row
              </th>
              <th scope="col" className="px-2 py-1.5">
                Name
              </th>
              <th scope="col" className="px-2 py-1.5">
                Address
              </th>
              <th scope="col" className="px-2 py-1.5">
                Type
              </th>
              <th scope="col" className="px-2 py-1.5">
                Check
              </th>
            </tr>
          </thead>
          <tbody>
            {preview.rows.map((r) => (
              <tr key={r.row} className={cn("border-t border-border", !r.record && "bg-danger/5")}>
                <td className="px-2 py-1 font-mono text-on-surface-muted">{r.row}</td>
                <td className="px-2 py-1 font-mono text-on-surface">{r.name || "—"}</td>
                <td className="px-2 py-1 font-mono text-on-surface-muted">{r.where}</td>
                <td className="px-2 py-1 font-mono text-on-surface-muted">{r.type || "—"}</td>
                <td className="px-2 py-1">
                  {r.record ? (
                    <span className="text-on-surface-muted">
                      {listed.has(pointIdentity(driverId, r.record.address)) ? "ok, already listed" : "ok"}
                    </span>
                  ) : (
                    <span className="text-danger">{r.errors.join("; ")}</span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <p className="text-xs text-on-surface-muted">
        Append keeps the list and adds these rows; a row for an address already listed updates it instead.
      </p>
      <div className="flex flex-wrap gap-2">
        <Button size="sm" disabled={valid.length === 0} onClick={() => onApply(valid, "replace")}>
          Replace rows
        </Button>
        <Button size="sm" variant="outline" disabled={valid.length === 0} onClick={() => onApply(valid, "append")}>
          Append rows
        </Button>
        <Button size="sm" variant="ghost" onClick={onCancel}>
          Cancel
        </Button>
      </div>
    </section>
  );
}

/** The word-order select, labelled by byte layout. */
export function WordOrderPicker({
  value,
  onChange,
  label,
}: {
  value: ModbusWordOrder;
  onChange: (w: ModbusWordOrder) => void;
  /** Accessible name ("Word order of holding 0"). */
  label: string;
}) {
  return (
    <NativeSelect
      aria-label={label}
      value={value}
      onChange={(e) => onChange(e.target.value as ModbusWordOrder)}
      title="How the device orders the bytes of a 32 or 64-bit value. If the sampled value looks wrong, try another order."
      className="h-7 w-32 px-1 font-mono text-xs"
    >
      {WORD_ORDER_CHOICES.map((w) => (
        <option key={w.value} value={w.value}>
          {w.label}
        </option>
      ))}
    </NativeSelect>
  );
}

/**
 * A scale typed as text, applied once it is a number other than 0 (empty
 * clears it), so half-typed input ("0.", "-") is never forced back.
 */
export function ScaleInput({
  label,
  value,
  onCommit,
}: {
  label: string;
  value: number | undefined;
  onCommit: (scale: number | null) => void;
}) {
  const [text, setText] = useState(value === undefined ? "" : String(value));
  const [seen, setSeen] = useState(value);
  if (value !== seen) {
    // Changed from outside (an import): show the new value.
    setSeen(value);
    const shown = text.trim() === "" ? undefined : Number(text);
    if (shown !== value) setText(value === undefined ? "" : String(value));
  }
  const n = Number(text);
  const invalid = text.trim() !== "" && (!Number.isFinite(n) || n === 0);
  return (
    <Input
      aria-label={label}
      inputMode="decimal"
      value={text}
      onChange={(e) => {
        const t = e.target.value;
        setText(t);
        const v = Number(t);
        if (t.trim() === "") onCommit(null);
        else if (Number.isFinite(v) && v !== 0) onCommit(v);
      }}
      placeholder="1"
      aria-invalid={invalid}
      title={invalid ? "A number other than 0; empty means 1." : "Sampled = decoded x scale"}
      className="h-7 w-16 font-mono text-xs"
    />
  );
}

/** "N selected · row types, word order and scale go live with them" and the step's continue button. */
export function MapFooter({ count, onContinue }: { count: number; onContinue?: () => void }) {
  return (
    <div
      role="status"
      aria-label="Selected points"
      className="sticky bottom-20 z-10 flex flex-wrap items-center gap-3 rounded-lg border border-border bg-surface-elevated/95 px-3 py-2 shadow-lg backdrop-blur"
    >
      <span className="flex min-w-0 flex-1 items-center gap-2 text-xs text-on-surface">
        <span
          className={cn(
            "flex h-5 min-w-5 items-center justify-center rounded-full px-1.5 text-[11px] font-medium",
            count > 0 ? "bg-brand text-brand-foreground" : "bg-surface-muted text-on-surface-muted",
          )}
        >
          {count}
        </span>
        <span>
          <span className="font-medium">{count} selected</span>
          <span className="text-on-surface-muted"> · row types, word order and scale go live with them</span>
        </span>
      </span>
      {onContinue && (
        <Button size="sm" onClick={onContinue} disabled={count === 0}>
          Bring in selected <ArrowRight />
        </Button>
      )}
    </div>
  );
}
